import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { AppShell } from "@/components/app-shell";

import { SignInPage } from "@/pages/sign-in.page";
import { SignUpPage } from "@/pages/sign-up.page";

import { HomePage } from "@/pages/home.page";
import { PipelinePage } from "@/pages/pipeline.page";
import { ReportPage } from "@/pages/report.page";
import { ExplorePage } from "@/pages/explore.page";
import { JobPage } from "@/pages/job.page";
import { PortalsPage } from "@/pages/portals.page";
import { PortalsProviderDetailPage } from "@/pages/portals-provider-detail.page";
import { AnalyticsPage } from "@/pages/analytics.page";
import { PipelineWorkflowPage } from "@/pages/pipeline-workflow.page";
import { JobsAddPage } from "@/pages/jobs-add.page";
import { JobDetailPage } from "@/pages/job-detail.page";
import { ApplyPage } from "@/pages/apply.page";
import { JobsPage } from "@/pages/jobs.page";
import { JobsDetailPage } from "@/pages/jobs-detail.page";
import { CompaniesPage } from "@/pages/companies.page";
import { CompaniesDetailPage } from "@/pages/companies-detail.page";
import { NetworkPage } from "@/pages/network.page";
import { IntakePage } from "@/pages/intake.page";
import { AiAssistantPage } from "@/pages/ai-assistant.page";
import { AssetsPage } from "@/pages/assets.page";

import { ProfileLayout } from "@/pages/profile/layout";
import { ProfileOverviewPage } from "@/pages/profile/overview.page";
import { ProfileAiContextPage } from "@/pages/profile/ai-context.page";
import { ProfileIntelligencePage } from "@/pages/profile/intelligence.page";
import { GoalsHubLayout } from "@/pages/goals/layout";
import { GoalsListPage } from "@/pages/goals/goals-list.page";
import { GoalDetailPage } from "@/pages/goals/goal-detail.page";
import { PlansListPage } from "@/pages/goals/plans-list.page";
import { PlanDetailPage } from "@/pages/goals/plan-detail.page";
import { WorkListPage } from "@/pages/goals/work-list.page";
import { WorkDetailPage } from "@/pages/goals/work-detail.page";

import { SettingsLayout } from "@/pages/settings/layout";
import { SettingsPromptsPage } from "@/pages/settings/prompts.page";
import { SettingsCliPage } from "@/pages/settings/cli.page";
import { SettingsProvidersPage } from "@/pages/settings/providers.page";
import { SettingsPluginsPage } from "@/pages/settings/plugins.page";
import { SettingsAiPage } from "@/pages/settings/ai.page";
import { SettingsPromptDetailPage } from "@/pages/settings/prompts/detail.page";
import { SettingsProviderDetailPage } from "@/pages/settings/providers/detail.page";
import { SettingsPluginDetailPage } from "@/pages/settings/plugins/detail.page";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  },
});

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function PipelineDetailRedirect() {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  return <Navigate to={`/pipeline/${pipelineId}`} replace />;
}

function PipelineIdRoute() {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  if (pipelineId && /^\d+$/.test(pipelineId)) {
    return <Navigate to={`/reports/${pipelineId}`} replace />;
  }
  return <PipelineWorkflowPage />;
}

function PipelineAddRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/jobs/add${search}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<ShellLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/jobs/add" element={<JobsAddPage />} />
              <Route path="/pipeline/add" element={<PipelineAddRedirect />} />
              <Route path="/pipeline/job/:id" element={<JobDetailPage />} />
              <Route path="/pipeline/detail/:pipelineId" element={<PipelineDetailRedirect />} />
              <Route path="/pipeline/:pipelineId" element={<PipelineIdRoute />} />
              <Route path="/reports/:id" element={<ReportPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/runs/:id" element={<JobPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/jobs/:id" element={<JobsDetailPage />} />
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/companies/:slug" element={<CompaniesDetailPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/network/intake" element={<IntakePage />} />
              <Route
                path="/portals"
                element={<Navigate to="/explore?sources=1" replace />}
              />
              <Route
                path="/portals/providers/:id"
                element={<Navigate to="/explore?mode=boards" replace />}
              />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/apply" element={<ApplyPage />} />
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/ai-assistant" element={<AiAssistantPage />} />

              <Route element={<GoalsHubLayout />}>
                <Route path="/goals" element={<GoalsListPage />} />
                <Route path="/goals/:goalId" element={<GoalDetailPage />} />
                <Route path="/plans" element={<PlansListPage />} />
                <Route path="/plans/:planId" element={<PlanDetailPage />} />
                <Route path="/work" element={<WorkListPage />} />
                <Route path="/work/:workId" element={<WorkDetailPage />} />
              </Route>

              <Route path="/profile" element={<ProfileLayout />}>
                <Route index element={<ProfileOverviewPage />} />
                <Route path="goals" element={<Navigate to="/goals" replace />} />
                <Route path="intelligence" element={<ProfileIntelligencePage />} />
                <Route path="ai-context" element={<ProfileAiContextPage />} />
              </Route>

              {/* Legacy redirects */}
              <Route path="/cv" element={<Navigate to="/profile/ai-context" replace />} />
              <Route path="/settings/profile" element={<Navigate to="/profile" replace />} />
              <Route path="/config" element={<Navigate to="/profile" replace />} />

              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/ai" replace />} />
                <Route path="prompts" element={<SettingsPromptsPage />} />
                <Route path="prompts/:kind" element={<SettingsPromptDetailPage />} />
                <Route path="cli" element={<SettingsCliPage />} />
                <Route path="ai" element={<SettingsAiPage />} />
                <Route path="providers" element={<SettingsProvidersPage />} />
                <Route path="providers/:id" element={<SettingsProviderDetailPage />} />
                <Route path="discovery" element={<PortalsPage />} />
                <Route
                  path="discovery/providers/:id"
                  element={<PortalsProviderDetailPage />}
                />
                <Route path="plugins" element={<SettingsPluginsPage />} />
                <Route path="plugins/:id" element={<SettingsPluginDetailPage />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
